import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Languages } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Reusable "Translate to ___" button for admin forms.
 *
 * Props:
 *   sourceContent: Object   — English JSON to translate (e.g., { title, vibe, description, facts })
 *   onTranslated: (esJson)  — callback receiving the Spanish JSON object
 *   targetLang: 'es' | 'fr' | ...  — default 'es'
 *   targetLabel: string     — label suffix, default 'Spanish'
 *   testId: string          — data-testid (default 'translate-btn')
 */
const TranslateButton = ({
  sourceContent,
  onTranslated,
  targetLang = 'es',
  targetLabel = 'Spanish',
  testId = 'translate-btn',
}) => {
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!sourceContent || Object.keys(sourceContent).length === 0) {
      toast.error('Nothing to translate yet — fill in the English fields first.');
      return;
    }
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/translate`,
        { content: sourceContent, target_lang: targetLang, source_lang: 'en' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.translated) {
        onTranslated(res.data.translated);
        toast.success(`Translated to ${targetLabel}. Review & edit before saving.`);
      } else {
        toast.error('Empty translation response.');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Translation failed';
      toast.error(`Translation failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      onClick={run}
      disabled={busy}
      variant="outline"
      className="border-terracotta text-terracotta hover:bg-terracotta/10 inline-flex items-center gap-2"
      data-testid={testId}
    >
      <Languages size={16} />
      {busy ? `Translating…` : `Translate to ${targetLabel}`}
    </Button>
  );
};

export default TranslateButton;
